// components/TransactionsLine.tsx
import { observer } from "mobx-react";
import React from "react";
import { Timeline, Text, Group, ActionIcon } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconAbacus,
} from "@tabler/icons-react";
import { Bob } from "../../stores/Bob";
import { TransactionListOutput } from "../../helpers/transactionsList";
import {
  TransactionsBullet,
  BULLET_SIZE,
  MAIN_ARROW_ICON_SIZE,
} from "./transactionsBullet";

interface TransactionsEntryProps {
  line: TransactionListOutput;
  bob: Bob;
}

const ACTION_ICON_SIZE = 20;
const ACTION_ICON_INNER = 14;

const truncate = (s: string, n: number) => {
  if (!s) return s;
  const cp = Array.from(s);
  return cp.length > n ? cp.slice(0, n).join("") + "…" : s;
};

const TransactionsLine = observer(({ line, bob }: TransactionsEntryProps) => {
  const isSent = !line.isCredit;
  const isPendingInvoice = line.isCredit && !line.isConfirmed;

  if (isPendingInvoice && line.zapName && line.zapName.trim().length > 0) {
    return null;
  }

  const arrowColor = (isSent || isPendingInvoice ? "gray" : "blue") as
    | "gray"
    | "blue";
  const isMobile = useMediaQuery("(max-width: 480px)");
  const primarySize = isMobile ? "sm" : "md";
  const subSize = isMobile ? "xs" : "sm";

  const primaryText =
    line.zapName && line.zapName.trim().length > 0
      ? truncate(line.zapName.trim(), 20)
      : line.label;

  const bullet =
    line.zapName && line.zapName.trim().length > 0 ? (
      <TransactionsBullet
        imgSrc={line.zapPictureUrl || null}
        bg="var(--mantine-color-dark-6)"
        badgeColor={arrowColor}
        withBadge
      />
    ) : (
      <TransactionsBullet
        imgSrc={null}
        bg={`var(--mantine-color-${arrowColor}-filled)`}
        badgeColor={arrowColor}
        centerIcon={
          isSent ? (
            <IconArrowUpRight size={MAIN_ARROW_ICON_SIZE} />
          ) : (
            <IconArrowDownRight size={MAIN_ARROW_ICON_SIZE} />
          )
        }
      />
    );

  return (
    <Timeline.Item
      bullet={bullet}
      style={{ ["--tl-bullet-size" as any]: `${BULLET_SIZE}px` }}
    >
      <Group
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: "8px",
          maxWidth: "100%",
          overflow: "hidden",
          padding: "0 12px",
        }}
      >
        <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
          <Text
            size={primarySize}
            style={{
              wordBreak: "break-word",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: `var(--mantine-color-${arrowColor}-text)`,
            }}
          >
            {primaryText}
          </Text>

          <Text size={subSize} c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {line.createdAtString}
          </Text>

          {line.lightningAddress && !line.isCredit && (
            <Text
              size={subSize}
              c="dimmed"
              style={{
                wordBreak: "break-word",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {line.lightningAddress}
            </Text>
          )}

          {line.comment && (
            <Text
              size={subSize}
              c="dimmed"
              style={{
                wordBreak: "break-word",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {`"${line.comment}"`}
            </Text>
          )}
        </div>

        <Group
          style={{
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "8px",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            <Text
              size={isMobile ? "md" : "lg"}
              style={{ color: `var(--mantine-color-${arrowColor}-text)` }}
            >
              {line.satoshisString}
            </Text>
            <Text size={subSize} c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {line.fiatString}
            </Text>
          </div>

          <ActionIcon
            variant="subtle"
            aria-label="data"
            color="gray"
            style={{
              flexShrink: 0,
              opacity: 0.85,
              width: ACTION_ICON_SIZE,
              height: ACTION_ICON_SIZE,
              minWidth: ACTION_ICON_SIZE,
            }}
            onClick={() =>
              bob.setSelectedTransactionToView({
                modelName: line.internalModelName,
                uuid: line.internalUuid,
              })
            }
          >
            <IconAbacus size={14} style={{ opacity: 0.75 }} stroke={1.5} />
          </ActionIcon>
        </Group>
      </Group>
    </Timeline.Item>
  );
});

export default TransactionsLine;
